import React, { useEffect, useRef, useState } from "react";

// Hooks
import { useAppDispatch } from "app/hooks";

// Store
import { toggleLoader } from "store/slice/UI";

// Config
import { paceData, tableHeaderConfig } from "./config";

// Types
import { SelectChangeEvent } from "@mui/material";
import { OrderTableBy, OrderTable } from "components/SortMenu/SortMenu";
import { debounce, isEmpty, isEqual } from "lodash";
import { initialPagination } from "constants/constants";
import { ConvertedAssessmentCentre, convertUserAssessmentList } from "./adapters/converters";
import { fetchAssessmentCentreUsers, TAssessmentCentreFiltersPayload } from "services/assessmentCentre";
import { normalizeAssessmentCentreFilters } from "./adapters/functions";
import { getTeams, Team } from "services";
import { generateID } from "utils";

type Filters = { filter_name: string; filter_pace_status: string[]; filter_teams: string[] };

const DEFAULT_PAGE_SIZE = 24;
const FILTERS_INITIAL_VALUE = { filter_name: "", filter_pace_status: ["ALL"], filter_teams: ["all"] };

const useAssessmentCentre = (): {
    users: ConvertedAssessmentCentre;
    filters: Filters;
    teamsData: Team[];
    usersSort: OrderTableBy;
    tableHeader: typeof tableHeaderConfig;
    inputName: string;
    inputPage: number | string;
    paginationTextInputRef: React.MutableRefObject<any>;
    disableAll: boolean;
    disableClearAll: boolean;
    filtersInitialValue: typeof FILTERS_INITIAL_VALUE;
    disabledReminderButton: boolean;
    handleSort: (direction: OrderTable, columnId: string | number) => void;
    handleChangeFilters: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[] | number[]>
    ) => void;
    handleClearFilters: (filterName?: "pace" | "teams" | "all") => void;
    toggleCheckUser: (event: React.ChangeEvent<HTMLInputElement>, userId: number) => void;
    toggleCheckAllUsers: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handlePageChange: (page: number) => Promise<void>;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnBlur: () => void;
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleShowReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleDownloadReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleTransferProfile: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleGetInfo: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleSendReminder: () => Promise<void>;
} => {
    const dispatch = useAppDispatch();

    const [users, setUsers] = useState<ConvertedAssessmentCentre>({
        data: [],
        meta: initialPagination,
        status: "pristine",
    });

    const [teamsData, setTeamsData] = useState<Team[]>([]);

    const [inputName, setInputName] = useState("");
    const [filters, setFilters] = useState<Filters>(FILTERS_INITIAL_VALUE);
    const [usersSort, setUsersSort] = useState<OrderTableBy>({ columnId: "name", direction: "default" });
    const [tableHeader, setTableHeader] = useState(tableHeaderConfig);
    const [currentSort, setCurrentSort] = useState<OrderTableBy>({ columnId: "name", direction: "default" });

    const previousFilters = useRef(FILTERS_INITIAL_VALUE);
    const disabledReminderButton = !users.data.some((user) => user.checked);

    const [inputPage, setInputPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    const disableAll = users.status === "fetching";
    const disableClearAll =
        disableAll ||
        Boolean(
            !Object.values(filters)
                .flat()
                .filter((el) => typeof el === "string" && el !== "" && el.toLowerCase() !== "all").length
        );

    const fetchTeams = async () => {
        try {
            const { data } = await getTeams();
            const teamsWithIds = data.teams.map((team) => ({
                ...team,
                id: generateID(),
                name: team.name,
            }));

            setTeamsData(teamsWithIds);
        } catch (error) {
            console.error(error);
        }
    };

    const handleFetchData = async (params?: Partial<TAssessmentCentreFiltersPayload>) => {
        if (isEqual(filters, previousFilters.current) && isEmpty(params)) {
            return;
        }

        const requestPayload = {
            ...normalizeAssessmentCentreFilters(filters, teamsData),
            order_by: `${currentSort.columnId},${currentSort.direction}`,
            page_size: DEFAULT_PAGE_SIZE,
            page_no: 1,
            ...(params ? normalizeAssessmentCentreFilters(params, teamsData) : {}),
        };

        try {
            setUsers((users) => ({ ...users, status: "fetching" }));
            const { data } = await fetchAssessmentCentreUsers(requestPayload);
            const convertedData = convertUserAssessmentList(data.results);
            setUsers({ data: convertedData, meta: data.meta, status: "done" });
            previousFilters.current = isEmpty(params) ? filters : FILTERS_INITIAL_VALUE;
        } catch (error) {
            console.error(error);
            setUsers((users) => ({ ...users, status: "error" }));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Filters and Sorting Functions
    // --------------------------------------------------------------------------------------------------------------
    const delayedFilterName = useRef(
        debounce((value: string) => {
            handleFetchData({ filter_name: value, page_size: DEFAULT_PAGE_SIZE, page_no: 1 });
        }, 500)
    ).current;
    useEffect(() => () => delayedFilterName.cancel(), [delayedFilterName]);

    const handleChangeFilters = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[] | number[]>
    ) => {
        setInputPage(1);

        if (event.target.name === "user-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setInputName(newValue);
            setFilters((currentVal) => ({ ...currentVal, filter_name: newValue }));
            delayedFilterName(newValue);
        }

        if (event.target.name === "pace-filter") {
            let newValue = event.target.value as string[];

            // If last selected value is ALL, reset selection to default
            if ((newValue.length > 1 && newValue.at(-1) === "ALL") || !newValue.length) {
                handleClearFilters("pace");
                return;
            }

            newValue = newValue.filter((value) => value !== "ALL");

            const updatedFilters = {
                ...filters,
                filter_pace_status: newValue,
                page_size: DEFAULT_PAGE_SIZE,
                page_no: 1,
            };
            setFilters(updatedFilters);
            handleFetchData(updatedFilters);
        }

        if (event.target.name === "teams-filter") {
            let newValue = event.target.value as string[];

            // If last selected value is 0, which is All (default), reset selection to default
            if ((newValue.length > 1 && newValue.at(-1) === "all") || !newValue.length) {
                handleClearFilters("teams");
                return;
            }

            newValue = newValue.filter((value) => value !== "all");

            const updatedFilters = {
                ...filters,
                filter_teams: newValue,
                page_size: DEFAULT_PAGE_SIZE,
                page_no: 1,
            };

            setFilters(updatedFilters);
            handleFetchData(updatedFilters);
        }
    };

    const handleClearFilters = (filterName?: "pace" | "teams" | "all") => {
        if (filterName === "all") {
            setInputName("");
            setFilters(FILTERS_INITIAL_VALUE);
            handleFetchData({ filter_name: "", filter_pace_status: [], filter_teams: [], page_no: 1 });
        }
        if (filterName === "pace") {
            setFilters((currentVal) => ({ ...currentVal, filter_pace_status: ["ALL"], page_no: 1 }));
            handleFetchData({ filter_pace_status: [] });
        }
        if (filterName === "teams") {
            setFilters((currentVal) => ({ ...currentVal, filter_teams: ["all"], page_no: 1 }));
            handleFetchData({ filter_teams: ["all"] });
        }
    };

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        setCurrentSort({ columnId, direction });
        handleFetchData({ order_by: `${columnId},${direction}` });
    };

    // --------------------------------------------------------------------------------------------------------------
    // Other Functions
    // --------------------------------------------------------------------------------------------------------------
    const toggleCheckUser = (event: React.ChangeEvent<HTMLInputElement>, userId: number) => {
        setUsers((users) => ({
            ...users,
            data: users.data.map((user) => ({
                ...user,
                checked: user.id === userId ? event.target.checked : user.checked,
            })),
        }));
    };

    const toggleCheckAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsers((users) => ({
            ...users,
            data: users.data.map((user) => ({
                ...user,
                checked: event.target.checked,
            })),
        }));
    };

    const handleSendReminder = async () => {
        console.log("send reminders");
    };

    // --------------------------------------------------------------------------------------------------------------
    // Pagination functions
    // --------------------------------------------------------------------------------------------------------------
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > users.meta.pages) {
            return;
        }
        setInputPage(page);
        handleFetchData({ page_size: users.meta.page_size, page_no: page });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setInputPage("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= users.meta.pages) {
            setInputPage(inputValue);
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputPage === users.meta.page_no) {
                return;
            }

            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setInputPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOnBlur = () => {
        if (inputPage === "" || inputPage === users.meta.page_no) {
            setInputPage(users.meta.page_no);
            return;
        }

        if (Boolean(inputPage) && Number(inputPage) > 0 && Number(inputPage) <= users.meta.pages) {
            previousPageValue.current = inputPage;
            handlePageChange(Number(inputPage));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // User row actions
    // --------------------------------------------------------------------------------------------------------------

    const handleShowReport = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    const handleDownloadReport = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    const handleTransferProfile = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    const handleGetInfo = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            dispatch(toggleLoader(true));

            if (!teamsData.length) {
                fetchTeams();
            }

            try {
                const { data } = await fetchAssessmentCentreUsers({ page_size: DEFAULT_PAGE_SIZE });
                const convertedData = convertUserAssessmentList(data.results);
                setUsers({ ...data, data: convertedData, status: "done" });
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        users,
        filters,
        teamsData,
        usersSort,
        tableHeader,
        inputName,
        inputPage,
        paginationTextInputRef,
        disableAll,
        disableClearAll,
        filtersInitialValue: FILTERS_INITIAL_VALUE,
        disabledReminderButton,
        handleSort,
        handleChangeFilters,
        handleClearFilters,
        toggleCheckUser,
        toggleCheckAllUsers,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        handleShowReport,
        handleDownloadReport,
        handleTransferProfile,
        handleGetInfo,
        handleSendReminder,
    };
};

export default useAssessmentCentre;
